import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import usePageMeta from "../hooks/usePageMeta";
import API from "../api.js";
import { BASE_URL } from "../config.js";
import {
  IDENTIFICATION_ACTION,
  recordIdentificationAction,
} from "./identificationActionApi";
import {
  appendKioskShiftStatusParams,
  buildKioskQrDataFromSearch,
  hasKioskCheckinContext,
  loadManageSelectedLocations,
  loadStoredManageContextRaw,
} from "./manageContext";
import { formatAppDateTime } from "../utils/appDateTime";
import {
  getClientIdFromShiftLike,
  loadClientShiftRoundingSettings,
  resolveShiftSettingsClientId,
} from "./shiftRounding";

/** Prefer signed/proxy avatar; direct S3 URLs often 403 or use wrong regional host. */
function extractProfileImageVersion(profileImageUrl) {
  if (!profileImageUrl || typeof profileImageUrl !== "string") return null;
  const m = profileImageUrl.match(/\/(\d+)\.(?:png|jpe?g|webp)(?:\?|$)/i);
  const n = m ? Number(m[1]) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
}

function staffAvatarDisplaySrc(accountId, profileImageUrl) {
  const id = Number(accountId);
  const base = BASE_URL;
  if (Number.isFinite(id) && id > 0 && base) {
    const v = extractProfileImageVersion(profileImageUrl);
    return v ? `${base}/api/avatars/account/${id}?v=${v}` : `${base}/api/avatars/account/${id}`;
  }
  if (profileImageUrl && String(profileImageUrl).trim()) {
    return String(profileImageUrl).trim();
  }
  return "";
}

function staffInitials(firstName, lastName) {
  const a = String(firstName || "").trim().charAt(0);
  const b = String(lastName || "").trim().charAt(0);
  const s = `${a}${b}`.toUpperCase();
  return s || "?";
}

// Helper to get current user's account ID
const getCurrentAccountId = () => {
  const accountId = localStorage.getItem("accountId");
  return accountId ? parseInt(accountId, 10) : null;
};

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  const accountId = getCurrentAccountId();
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (accountId) headers['x-author-account-id'] = `${accountId}`;
  return headers;
};

// Safe helper to stop active MediaStream tracks without requesting new hardware sessions
const stopStreamTracks = (stream) => {
  if (!stream) return;
  try {
    const tracks = stream.getTracks ? stream.getTracks() : [];
    tracks.forEach((track) => {
      try {
        track.stop();
        track.enabled = false;
      } catch (_) {}
    });
  } catch (_) {}
};

/** True when the browser may expose Geolocation (HTTPS, localhost, or 127.0.0.1). */
function isGeolocationAllowedContext() {
  if (typeof window === "undefined") return false;
  if (window.isSecureContext) return true;
  const h = window.location?.hostname;
  return h === "localhost" || h === "127.0.0.1";
}

/**
 * Device GPS for Admin map link (required for VERIFY). Rejects with err.code GEO_* on failure.
 * Map coordinates come from here, not IP geolocation.
 */
/** Public IP as seen from the internet (for Admin audit when API only receives ::1 on localhost). */
async function getClientPublicIp(timeoutMs = 4000) {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const res = await fetch("https://api.ipify.org?format=json", {
      signal: controller.signal,
    });
    clearTimeout(timer);
    if (!res.ok) return null;
    const data = await res.json();
    const ip = typeof data?.ip === "string" ? data.ip.trim() : "";
    return ip || null;
  } catch {
    return null;
  }
}

function requestGeolocationOnce(timeoutMs, enableHighAccuracy) {
  return new Promise((resolve, reject) => {
    if (!isGeolocationAllowedContext()) {
      reject(
        Object.assign(new Error("Geolocation requires a secure context (HTTPS)."), {
          code: "GEO_INSECURE_CONTEXT",
        }),
      );
      return;
    }
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      reject(
        Object.assign(new Error("Geolocation is not supported."), { code: "GEO_NOT_SUPPORTED" }),
      );
      return;
    }

    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(Object.assign(new Error("Geolocation timed out."), { code: "GEO_TIMEOUT" }));
    }, timeoutMs);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        const lat = pos?.coords?.latitude;
        const lng = pos?.coords?.longitude;
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          resolve({ scanLat: lat, scanLng: lng });
        } else {
          reject(
            Object.assign(new Error("Position unavailable."), {
              code: "GEO_POSITION_UNAVAILABLE",
            }),
          );
        }
      },
      (posErr) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        const c = posErr?.code;
        if (c === 1) {
          reject(
            Object.assign(new Error("Permission denied."), { code: "GEO_PERMISSION_DENIED" }),
          );
        } else if (c === 2) {
          reject(
            Object.assign(new Error("Position unavailable."), {
              code: "GEO_POSITION_UNAVAILABLE",
            }),
          );
        } else if (c === 3) {
          reject(Object.assign(new Error("Geolocation timed out."), { code: "GEO_TIMEOUT" }));
        } else {
          reject(Object.assign(new Error("Geolocation failed."), { code: "GEO_UNKNOWN" }));
        }
      },
      {
        enableHighAccuracy,
        maximumAge: 0,
        timeout: timeoutMs,
      },
    );
  });
}

/**
 * Same contract/behavior as before (resolves {scanLat, scanLng} or rejects with a GEO_* code),
 * but retries once on a transient failure before giving up. iOS Safari's location stack is
 * slower/flakier than Chrome's under enableHighAccuracy, so a lone failed/timed-out attempt no
 * longer fails the whole scan outright. Permission-denied is not retried (retrying can't fix that).
 */
async function getScanGeolocation(timeoutMs = 15000) {
  try {
    return await requestGeolocationOnce(timeoutMs, true);
  } catch (err) {
    if (err?.code === "GEO_PERMISSION_DENIED") throw err;
    try {
      return await requestGeolocationOnce(timeoutMs, false);
    } catch (retryErr) {
      throw retryErr;
    }
  }
}

function mapGeolocationScanError(err) {
  const code = err?.code;
  if (code === "GEO_PERMISSION_DENIED") {
    return "Location access is required for the map pin. Allow location for this site, then try VERIFY again.";
  }
  if (code === "GEO_TIMEOUT" || code === "GEO_UNKNOWN") {
    return "Could not read your location in time. Try again with a clearer GPS signal.";
  }
  if (code === "GEO_POSITION_UNAVAILABLE") {
    return "Your device could not determine location. Turn on location services and try again.";
  }
  if (code === "GEO_NOT_SUPPORTED") {
    return "This browser does not support location. Try another browser or device.";
  }
  if (code === "GEO_INSECURE_CONTEXT") {
    return "Open this page over HTTPS (or http://localhost) so the browser can access your location.";
  }
  return err?.message || "Location is required but could not be read. Please try again.";
}

async function parseFaceApiErrorResponse(response) {
  if (response.status === 504) return "Face verification timed out";
  const j = await response.json().catch(() => ({}));
  if (j && typeof j.error === "string" && j.error.trim()) return j.error.trim();
  return `Face verification failed (${response.status})`;
}

function getLocationNameFromShiftLike(shiftLike) {
  return (
    shiftLike?.location ||
    shiftLike?.locationName ||
    shiftLike?.location_name ||
    shiftLike?.clientLocationName ||
    shiftLike?.client_location_name ||
    ""
  );
}

function normalizeScopeValue(value) {
  return String(value || "").trim().toLowerCase();
}

function nextShiftMatchesKioskScope(nextShift, urlParams) {
  if (!nextShift) return false;
  const urlClientId = urlParams.get("clientId");
  const urlLocation = urlParams.get("location");
  const nextClientId = getClientIdFromShiftLike(nextShift);
  const nextLocation = getLocationNameFromShiftLike(nextShift);

  if (urlClientId && nextClientId && String(urlClientId) !== String(nextClientId)) {
    return false;
  }
  if (urlLocation && nextLocation) {
    return normalizeScopeValue(urlLocation) === normalizeScopeValue(nextLocation);
  }
  return true;
}

function buildIdentifiedShiftState(statusData, urlParams) {
  const hasShift = Boolean(statusData?.hasShift || statusData?.hasScheduledShift);
  const nextShift = statusData?.nextShiftDetails || null;
  const shouldUseNextShift =
    !hasShift &&
    nextShiftMatchesKioskScope(nextShift, urlParams) &&
    !statusData?.isCheckedIn;

  return {
    hasScheduledShift: hasShift || shouldUseNextShift,
    scheduledShiftDetails:
      statusData?.shiftDetails ||
      statusData?.scheduledShiftDetails ||
      (shouldUseNextShift ? nextShift : null),
    isCheckedIn: shouldUseNextShift ? false : Boolean(statusData?.isCheckedIn),
    isCheckedOut: shouldUseNextShift ? false : Boolean(statusData?.isCheckedOut),
    nextShiftDetails: shouldUseNextShift ? null : nextShift,
    checkedInShiftDetails: statusData?.checkedInShiftDetails || null,
    checkedInShift: statusData?.checkedInShift || null,
    currentShiftDetails: statusData?.currentShiftDetails || null,
    currentShift: statusData?.currentShift || null,
    activeShiftDetails: statusData?.activeShiftDetails || null,
    activeShift: statusData?.activeShift || null,
    hasAmbiguousShiftChoice: Boolean(statusData?.hasAmbiguousShiftChoice),
    openShiftOption: statusData?.openShiftOption || null,
    todayShiftOption: statusData?.todayShiftOption || null,
  };
}

const NOT_SCHEDULED_HERE_MESSAGE =
  "The check-in location has been changed. Please set the device to the correct check-in location.";

/** Client location id of the shift being checked in to / out of. */
function getActionShiftClientLocationId(user) {
  const candidates = [
    user?.scheduledShiftDetails,
    user?.checkedInShiftDetails,
    user?.checkedInShift,
    user?.currentShiftDetails,
    user?.currentShift,
    user?.activeShiftDetails,
    user?.activeShift,
  ];
  for (const shift of candidates) {
    const id = shift?.clientLocationId ?? shift?.client_location_id;
    if (id != null && String(id).trim() !== "") return String(id).trim();
  }
  return null;
}

/**
 * True when the shift location is one of this device's sign-off locations
 * (manageSelectedLocations for the current client/device). No stored locations blocks everyone.
 */
function isShiftLocationAllowedOnDevice(user, searchString) {
  const params = new URLSearchParams(searchString || "");
  const storedContext = loadStoredManageContextRaw();
  const clientId = params.get("clientId") || storedContext?.clientId || null;
  const clientDeviceId = params.get("clientDeviceId") || storedContext?.clientDeviceId || null;
  const allowedIds = loadManageSelectedLocations({ clientId, clientDeviceId }).map((id) =>
    String(id).trim(),
  );
  const shiftLocationId = getActionShiftClientLocationId(user);
  console.log("[face-scan][location-restriction]", {
    clientId,
    clientDeviceId,
    allowedIds,
    shiftLocationId,
  });
  if (!allowedIds.length || !shiftLocationId) return false;
  return allowedIds.includes(shiftLocationId);
}

function resolveFaceScanShiftSettingsClientId(payload, fallbackClientId) {
  return resolveShiftSettingsClientId({
    payload,
    qrData: payload?.qrData,
    urlClientId: fallbackClientId,
  });
}

export default function FaceScan() {
  usePageMeta("Face Scan");
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [allowedAccountIds, setAllowedAccountIds] = useState(new Set());
  const [timesheetLoading, setTimesheetLoading] = useState(false);
  const [accountInput, setAccountInput] = useState("");
  const [eligibilityMessage, setEligibilityMessage] = useState("");
  const [searchParams] = useSearchParams();
  const [qrData, setQrData] = useState(null);
  const [identifiedUser, setIdentifiedUser] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmAvatarFailed, setConfirmAvatarFailed] = useState(false);
  const [locationBlockedMessage, setLocationBlockedMessage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingAction, setProcessingAction] = useState(null); // 'checkin' or 'checkout'
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [cameraStatus, setCameraStatus] = useState("Starting camera...");
  /** Frozen JPEG snapshot shown while identify API runs (legacy sign-in scan parity). */
  const [frozenPreview, setFrozenPreview] = useState(null);
  const [isInQrMode, setIsInQrMode] = useState(() => {
    // Initialize based on URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('qrToken');
  });
  const isMountedRef = useRef(true);

  // Helper function to check if we're in QR mode
  const isQrMode = () => {
    return isInQrMode;
  };

  const hasReadyVideoFrame = () => {
    const video = videoRef.current;
    return !!(video && video.readyState >= 2 && video.videoWidth >= 240 && video.videoHeight >= 240);
  };

  const clearFrozenPreview = useCallback(() => {
    setFrozenPreview(null);
  }, []);

  const pauseLiveCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
        track.enabled = false;
      });
      streamRef.current = null;
    }
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject = null;
    }
    setIsCameraReady(false);
    setCameraStatus("Photo captured");
  }, []);

  const startCamera = useCallback(async () => {
    try {
      setIsCameraReady(false);
      setCameraStatus("Starting camera...");
      let mediaStream;
      try {
        // Legacy sign-in scan: front camera — full feed shown (no crop guide).
        mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: "user",
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        });
      } catch (primaryErr) {
        console.warn("Primary camera constraints failed, using default:", primaryErr);
        mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: "user" },
        });
      }

      if (!isMountedRef.current) {
        stopStreamTracks(mediaStream);
        return;
      }

      streamRef.current = mediaStream;
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        const markReady = () => {
          const ready = hasReadyVideoFrame();
          setIsCameraReady(ready);
          setCameraStatus(ready ? "Camera ready" : "Preparing camera...");
        };
        videoRef.current.onloadedmetadata = markReady;
        videoRef.current.oncanplay = markReady;
        videoRef.current.onplaying = markReady;
        videoRef.current.play().catch((err) => {
          if (err.name !== "AbortError" && err.name !== "NotAllowedError") {
            console.warn("Video play error:", err);
          }
        });
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      if (isMountedRef.current) {
        setIsCameraReady(false);
        setCameraStatus("Camera unavailable");
        setError("Unable to access camera. Please check permissions.");
      }
    }
  }, []);

  const restartLiveCamera = useCallback(async () => {
    clearFrozenPreview();
    setError("");
    isMountedRef.current = true;
    await startCamera();
  }, [clearFrozenPreview, startCamera]);

  const mapVerificationError = (err) => {
    if (err?.code && String(err.code).startsWith("GEO_")) {
      return mapGeolocationScanError(err);
    }
    const message = err?.message || "";
    if (message.includes("Camera is not ready") || message.includes("capture image from camera")) {
      return "Camera is still starting. Wait a moment, then try again.";
    }
    if (message.includes("timed out")) {
      return "Face verification timed out. Please try again in a moment.";
    }
    if (message.includes("No face detected")) {
      return "No face was detected. Center your face in the guide and try again.";
    }
    return err?.response?.data?.error || message || "Face verification failed";
  };

  useEffect(() => {
    isMountedRef.current = true;

    // Face check is completely independent of login - ignore any logged-in account
    // QR mode: compares against ALL profiles equally, including logged-in account (no special treatment)
    // Non-QR mode: also compares against ALL profiles
    if (!isQrMode()) {
      console.log("Proceeding with face verification (no login required, all profiles checked equally)");
    } else {
      console.log("Proceeding with QR face identification (no login required, all profiles checked equally - logged-in account has no preference)");
    }

    // Check for QR token first (unified QR + face recognition flow)
    (async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const qrToken = urlParams.get('qrToken');

        if (qrToken) {
          // Unified flow: Validate QR token and extract shift/timesheet data
          setTimesheetLoading(true);
          console.log('Processing QR token for unified check-in');

          const response = await fetch(`${BASE_URL}/api/shifts/qr-checkin`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ token: qrToken })
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'QR validation failed' }));
            setError(errorData.message || 'Invalid QR code or no assignment found');
            setTimesheetLoading(false);
            // Keep in QR mode but show error - don't fall back to login mode
            return;
          }

          const result = await response.json();
          console.log('QR validation successful:', result);

          // Store QR data for use in face identification and check-in
          setQrData(result);
          localStorage.setItem('lastQrData', JSON.stringify(result));

          // QR validation succeeded - proceed with face recognition
          setEligibilityMessage('QR validated. Please position your face in the camera.');
          setTimesheetLoading(false);
          return;
        }

        // Fallback: process timesheet filters (legacy behavior)
        const timesheetId = urlParams.get('timesheetId');
        if (timesheetId) {
          // Prefer validating a single timesheet: ask backend which account is assigned
          setTimesheetLoading(true);
          const base = BASE_URL;
          const res = await fetch(`${base}/api/timesheets/validate-access?timesheetId=${encodeURIComponent(timesheetId)}`, {
            headers: { ...getAuthHeaders() },
          });
          if (!res.ok) {
            console.warn('Failed to fetch timesheet assignment', res.status);
            setTimesheetLoading(false);
            return;
          }
          const body = await res.json();
          const assigned = body?.assignedAccount || null;
          const ids = new Set();
          if (assigned) ids.add(Number(assigned));
          setAllowedAccountIds(ids);
          console.log('Checkin: allowed account ids from timesheetId', Array.from(ids));
          setTimesheetLoading(false);
          return;
        }

        const from = urlParams.get('from');
        const to = urlParams.get('to');
        const client = urlParams.get('client');
        const location = urlParams.get('location');
        const params = new URLSearchParams();
        if (from) params.set('from', from);
        if (to) params.set('to', to);
        if (client) params.set('client', client);
        if (location) params.set('location', location);
        if (!params.toString()) return;

        setTimesheetLoading(true);
        const base = BASE_URL;
        const res2 = await fetch(`${base}/api/timesheets?${params.toString()}`);
        if (!res2.ok) {
          console.warn('Failed to fetch timesheets for checkin context', res2.status);
          setTimesheetLoading(false);
          return;
        }
        const body = await res2.json();
        const rows = body?.data || body || [];
        const ids = new Set();
        (rows || []).forEach(r => {
          const possible = r.account_id || r.staff_id || r.staffId || r.staffId || r.staffId || r.staffId || r.staffId || r.staffId || r.staffId || r.staffId || r.staffId || r.staffId || r.staffId || r.account_id || r.staffId || r.staffId || r.staffId;
          // fallback: many shapes — try common properties
          const candidate = r.account_id || r.staffId || r.staff_id || r.staff || r.staffId || r.staffId || r.staffId || null;
          if (candidate) ids.add(Number(candidate));
        });
        setAllowedAccountIds(ids);
        console.log('Checkin: allowed account ids from timesheets', Array.from(ids));
      } catch (e) {
        console.warn('Error fetching timesheets for checkin context', e);
      } finally {
        setTimesheetLoading(false);
      }
    })();

    startCamera();

    // Prime the location permission/OS fix as soon as the page opens, in parallel with the
    // camera starting, instead of waiting until VERIFY is tapped. Fire-and-forget: any error
    // here is intentionally swallowed — it doesn't set any error state or affect the UI. This
    // only gives the real getScanGeolocation() call inside handleVerify a head start (prompt
    // already resolved, position possibly already cached) so it's less likely to time out.
    getScanGeolocation().catch(() => {});

    // Handle page visibility change (tab switch, iPad screen wake/sleep)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (streamRef.current) {
          stopStreamTracks(streamRef.current);
          streamRef.current = null;
        }
        if (videoRef.current?.srcObject) {
          stopStreamTracks(videoRef.current.srcObject);
          videoRef.current.srcObject = null;
        }
        setIsCameraReady(false);
      } else if (isMountedRef.current && !showConfirmation && !frozenPreview) {
        // iPad screen woke up or user returned to tab — restart camera
        startCamera();
      }
    };

    // Handle Safari BFCache restoration
    const handlePageShow = (e) => {
      if (e?.persisted && isMountedRef.current && !showConfirmation && !frozenPreview) {
        startCamera();
      }
    };

    // Handle before unload
    const handleBeforeUnload = () => {
      if (streamRef.current) {
        stopStreamTracks(streamRef.current);
        streamRef.current = null;
      }
      if (videoRef.current?.srcObject) {
        stopStreamTracks(videoRef.current.srcObject);
        videoRef.current.srcObject = null;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handleBeforeUnload);

    // Cleanup: stop camera when component unmounts (navigating away)
    return () => {
      isMountedRef.current = false;
      
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleBeforeUnload);
      
      // Force stop all tracks
      if (streamRef.current) {
        stopStreamTracks(streamRef.current);
        streamRef.current = null;
      }
      
      // Clear video element
      if (videoRef.current) {
        try {
          if (videoRef.current.srcObject) {
            stopStreamTracks(videoRef.current.srcObject);
          }
          videoRef.current.srcObject = null;
        } catch (e) {
          console.error("Error cleaning up video:", e);
        }
      }
      
      setIsCameraReady(false);
      setCameraStatus("Camera stopped");
    };
  }, [startCamera, showConfirmation, frozenPreview]);

  const stopCamera = () => {
    isMountedRef.current = false;
    setFrozenPreview(null);

    if (streamRef.current) {
      stopStreamTracks(streamRef.current);
      streamRef.current = null;
    }
    
    if (videoRef.current) {
      try {
        if (videoRef.current.srcObject) {
          stopStreamTracks(videoRef.current.srcObject);
        }
        videoRef.current.srcObject = null;
      } catch (e) {
        console.error("Error stopping camera:", e);
      }
    }
    
    setIsCameraReady(false);
    setCameraStatus("Camera stopped");
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      
      // Ensure video is ready and has valid dimensions
      if (!video.videoWidth || !video.videoHeight || video.videoWidth === 0 || video.videoHeight === 0) {
        console.error("Video not ready or invalid dimensions:", {
          width: video.videoWidth,
          height: video.videoHeight,
          readyState: video.readyState
        });
        return null;
      }
      
      // Ensure video is playing (readyState >= 2 means HAVE_CURRENT_DATA)
      if (video.readyState < 2) {
        console.warn("Video not ready, readyState:", video.readyState);
      }
      
      const canvas = canvasRef.current;
      
      const maxDimension = 960;
      const scale = Math.min(1, maxDimension / video.videoWidth, maxDimension / video.videoHeight);
      canvas.width = Math.max(400, Math.round(video.videoWidth * scale));
      canvas.height = Math.max(400, Math.round(video.videoHeight * scale));
      
      const ctx = canvas.getContext('2d');
      
      // Ensure minimum dimensions for face detection (400x400)
      const minDimension = 400;
      if (canvas.width < minDimension || canvas.height < minDimension) {
        // Scale up if too small, maintaining aspect ratio
        const scale = Math.max(minDimension / canvas.width, minDimension / canvas.height);
        canvas.width = Math.round(canvas.width * scale);
        canvas.height = Math.round(canvas.height * scale);
      }
      
      // Draw video frame to canvas with better quality settings
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Convert to a smaller JPEG to keep verification fast and reliable.
      const dataUrl = canvas.toDataURL('image/jpeg', 0.72);
      
      console.log("Captured image:", {
        width: canvas.width,
        height: canvas.height,
        dataUrlLength: dataUrl.length,
        videoWidth: video.videoWidth,
        videoHeight: video.videoHeight
      });
      
      return dataUrl;
    }
    return null;
  };

  /** Capture one frame, show it, stop live video — identification uses this image only. */
  const freezeCaptureFromCamera = () => {
    if (!hasReadyVideoFrame()) return null;
    const dataUrl = capturePhoto();
    if (!dataUrl || dataUrl.length < 1000) return null;
    setFrozenPreview(dataUrl);
    pauseLiveCamera();
    return dataUrl;
  };

  const handleVerify = async () => {
    // Check if we're in QR mode by looking for qrToken in URL
    const urlParams = new URLSearchParams(window.location.search);
    const isInQrMode = urlParams.has('qrToken');

    // If in QR mode, use unified QR + face identification flow (bypasses all login/timesheet filtering)
    if (isInQrMode) {
      return handleQrFaceIdentification();
    }

    // Traditional flow: allow face recognition even without login or timesheet restrictions
    setIsVerifying(true);
    setError("");

    let capturedImage;
    try {
      if (!frozenPreview && !hasReadyVideoFrame()) {
        throw new Error("Camera is not ready yet.");
      }

      capturedImage = frozenPreview || freezeCaptureFromCamera();
      if (!capturedImage) {
        throw new Error("Failed to capture image from camera. Please ensure the camera is active and your face is visible.");
      }

      const [{ scanLat, scanLng }, clientIp] = await Promise.all([
        getScanGeolocation(),
        getClientPublicIp(),
      ]);
      const identificationSubmittedAt = new Date().toISOString();

      console.log("Sending face verification request (no login required)");

      // Use /identify endpoint which compares against all profiles (no auth required)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 20000);
      const response = await fetch(`${BASE_URL}/api/face/identify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        signal: controller.signal,
        body: JSON.stringify({
          imageBase64: capturedImage,
          clientDeviceId: searchParams.get("clientDeviceId") || null,
          clientId: searchParams.get("clientId") || null,
          scanLat,
          scanLng,
          clientIp,
          identificationSubmittedAt,
          actualActionTimestamp: identificationSubmittedAt,
        })
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(await parseFaceApiErrorResponse(response));
      }

      const data = await response.json();
      console.log("Face verification response:", data);

      // Stop camera before navigating
      stopCamera();

      // Small delay to ensure camera stops before navigation
      setTimeout(() => {
        // Handle /identify response format (no account/login info used)
        const matched = data.matched || (data.success && data.accountId);
        // Use accountId from response only (identified by face, not from login)
        const accountId = data.accountId;
        
        if (data.success && matched) {
          // Face matched! Check shift status and show confirmation
          // For non-QR flow, we need to check if user has shifts today
          checkShiftStatusAndShowConfirmation(accountId, {
            capturedImage,
            accountId: accountId,
            similarity: data.similarity,
            firstName: data.firstName,
            lastName: data.lastName,
            profileImageUrl: data.profileImageUrl,
            identificationId: data.identificationId,
            identificationSubmittedAt,
            actualActionIso: identificationSubmittedAt,
          });
        } else {
          // Face not recognized - include error message for better feedback
          navigate("/checkin/face-not-recognized" + window.location.search, {
            state: {
              capturedImage,
              reason: data.reason || "FACE_NOT_MATCHED",
              message: data.error || data.message || "Face not recognized. Please ensure your face is clearly visible and try again.",
              identificationId: data.identificationId
            }
          });
        }
      }, 200);

    } catch (err) {
      console.error("Face verification error:", err);
      const msg = mapVerificationError(err);
      setError(msg);
      if (err?.code && String(err.code).startsWith("GEO_")) {
        await restartLiveCamera();
        return;
      }
      stopCamera();
      setTimeout(() => {
        navigate("/checkin/face-not-recognized" + window.location.search, {
          state: {
            capturedImage: capturedImage ?? frozenPreview,
            error: msg,
          },
        });
      }, 200);
    } finally {
      setIsVerifying(false);
    }
  };

  // Check shift status for non-QR flow
  const checkShiftStatusAndShowConfirmation = async (accountId, userData) => {
    try {
      const search = window.location.search;
      const urlParams = new URLSearchParams(search);
      const timesheetIdFromUrl = urlParams.get("timesheetId");
      const statusQs = new URLSearchParams({ accountId: String(accountId) });
      if (timesheetIdFromUrl) statusQs.set("timesheetId", timesheetIdFromUrl);
      const clientDeviceIdFromUrl = urlParams.get("clientDeviceId");
      if (clientDeviceIdFromUrl) statusQs.set("clientDeviceId", clientDeviceIdFromUrl);
      appendKioskShiftStatusParams(statusQs, search);

      const response = await fetch(
        `${BASE_URL}/api/face/check-shift-status?${statusQs.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const shiftData = await response.json();
      const shiftClientId = resolveFaceScanShiftSettingsClientId(
        shiftData,
        urlParams.get("clientId"),
      );
      const shiftRoundingSettings = await loadClientShiftRoundingSettings(shiftClientId);
      const identifiedShiftState = buildIdentifiedShiftState(shiftData, urlParams);

      const identifiedUserData = {
        ...userData,
        ...identifiedShiftState,
        policy: shiftData.policy || null,
        checkinOptions: shiftData.checkinOptions || null,
        ui: shiftData.ui || null,
        shiftRoundingSettings,
        hasAmbiguousShiftChoice: Boolean(shiftData?.hasAmbiguousShiftChoice),
        openShiftOption: shiftData?.openShiftOption || null,
        todayShiftOption: shiftData?.todayShiftOption || null,
      };

      setIdentifiedUser(identifiedUserData);
      setShowConfirmation(true);
      setIsVerifying(false);
    } catch (err) {
      console.error("Error checking shift status:", err);
      // If shift check fails, still show confirmation but without shift info
      const identifiedUserData = {
        ...userData,
        hasScheduledShift: false,
        isCheckedIn: false,
        isCheckedOut: false
      };
      setIdentifiedUser(identifiedUserData);
      setShowConfirmation(true);
      setIsVerifying(false);
    }
  };

  const handleQrFaceIdentification = async () => {
    // Double-check we're actually in QR mode
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.has('qrToken')) {
      console.error('ERROR: handleQrFaceIdentification called but no qrToken in URL');
      setError("Invalid access. Please scan a QR code to access this system.");
      return;
    }

    // Ensure we have QR data before proceeding
    if (!qrData) {
      setError("QR code validation incomplete. Please refresh and scan the QR code again.");
      return;
    }

    setIsVerifying(true);
    setError("");

    let capturedImage;
    try {
      if (!frozenPreview && !hasReadyVideoFrame()) {
        throw new Error("Camera is not ready yet.");
      }

      capturedImage = frozenPreview || freezeCaptureFromCamera();
      if (!capturedImage) {
        throw new Error("Failed to capture image from camera");
      }

      const [{ scanLat, scanLng }, clientIp] = await Promise.all([
        getScanGeolocation(),
        getClientPublicIp(),
      ]);
      const identificationSubmittedAt = new Date().toISOString();

      console.log("Sending QR face identification request (no account/login info used)...");

      // Use the unified QR face identification endpoint
      // IMPORTANT: No account ID, no auth headers, no login info - only the scanned image
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 20000);
      const response = await fetch(`${BASE_URL}/api/face/qr-identify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
          // NO auth headers, NO account headers - completely anonymous
        },
        signal: controller.signal,
        body: JSON.stringify({
          imageBase64: capturedImage,
          shiftId: qrData.shiftId,
          timesheetId: qrData.timesheetId,
          shiftDetails: qrData.shiftDetails,
          clientDetails: qrData.clientDetails,
          timesheetDetails: qrData.timesheetDetails,
          clientDeviceId: searchParams.get("clientDeviceId") || null,
          clientId: searchParams.get("clientId") || null,
          shiftDate:
            searchParams.get("shiftDate") ||
            searchParams.get("from") ||
            qrData?.shiftDetails?.startDate ||
            null,
          scanLat,
          scanLng,
          clientIp,
          identificationSubmittedAt,
          actualActionTimestamp: identificationSubmittedAt,
          // NO accountId - system will identify from face only
        })
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(await parseFaceApiErrorResponse(response));
      }

      const data = await response.json();
      console.log("QR face identification response:", data);

      if (data.success && data.identified) {
        // Face identified! Show confirmation dialog asking "Is this you?"
        const shiftClientId = resolveFaceScanShiftSettingsClientId(
          { ...data, qrData },
          searchParams.get("clientId"),
        );
        const shiftRoundingSettings = await loadClientShiftRoundingSettings(shiftClientId);
        const identifiedShiftState = buildIdentifiedShiftState(data, searchParams);
        const identifiedUser = {
          accountId: data.accountId,
          firstName: data.firstName,
          lastName: data.lastName,
          similarity: data.similarity,
          ...identifiedShiftState,
          profileImageUrl: data.profileImageUrl,
          identificationId: data.identificationId,
          identificationSubmittedAt,
          actualActionIso: identificationSubmittedAt,
          shiftRoundingSettings,
        };

        setIdentifiedUser(identifiedUser);
        setShowConfirmation(true);
        setIsVerifying(false);
      } else {
        // Face not identified
        stopCamera();
        setTimeout(() => {
          navigate("/checkin/qr-face-not-identified" + window.location.search, {
            state: {
              qrData,
              capturedImage,
              reason: data.reason || "FACE_NOT_IDENTIFIED",
              message: data.error || "We couldn't identify you. Please try again.",
              assignedStaff: data.assignedStaff,
              identificationId: data.identificationId
            }
          });
        }, 200);
      }

    } catch (err) {
      console.error("QR face identification error:", err);
      const msg = mapVerificationError(err);
      setError(msg);
      if (err?.code && String(err.code).startsWith("GEO_")) {
        await restartLiveCamera();
        return;
      }
      stopCamera();
      setTimeout(() => {
        navigate("/checkin/qr-face-not-identified" + window.location.search, {
          state: {
            qrData,
            capturedImage: capturedImage ?? frozenPreview,
            error: msg,
          },
        });
      }, 200);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleConfirmIdentity = async (confirmed) => {
    if (!confirmed) {
      if (identifiedUser?.identificationId) {
        await recordIdentificationAction(identifiedUser.identificationId, {
          action: IDENTIFICATION_ACTION.NOT_FOUND,
          status: "NOT ME",
        });
      }

      stopCamera();

      navigate("/checkin/face-not-recognized" + window.location.search, {
        state: {
          capturedImage: frozenPreview,
          reason: "FACE_NOT_MATCHED",
          message: "The face scan didn't match your stored profile picture.",
          identificationId: identifiedUser?.identificationId
        }
      });
      return;
    }

    // User confirmed "Yes, it's me" — route based on shift assignment at this check-in point
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
        track.enabled = false;
      });
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    const search = window.location.search;
    const effectiveQrData = qrData || buildKioskQrDataFromSearch(search);

    if (
      !identifiedUser?.hasScheduledShift &&
      (effectiveQrData || hasKioskCheckinContext(search))
    ) {
      navigate("/checkin/qr-no-shift" + search, {
        state: {
          qrData: effectiveQrData,
          user: identifiedUser,
        },
      });
      return;
    }

    // Normal face scan only: block check-in/out when the shift location is not one of this
    // device's sign-off locations.
    if (!qrData && !isShiftLocationAllowedOnDevice(identifiedUser, search)) {
      setLocationBlockedMessage(NOT_SCHEDULED_HERE_MESSAGE);
      return;
    }

    const identificationSubmittedAt =
      identifiedUser?.identificationSubmittedAt ||
      identifiedUser?.actualActionIso ||
      new Date().toISOString();
    navigate("/checkin/shift-details" + search, {
      state: {
        qrData: effectiveQrData,
        user: {
          ...identifiedUser,
          identificationSubmittedAt,
          actualActionIso: identificationSubmittedAt,
        },
      },
    });
  };


  const handleAccountSubmit = () => {
    const val = Number(accountInput);
    if (!val) {
      setEligibilityMessage('Please enter a valid numeric Account ID');
      return;
    }
    // If QR provided a timesheetId, validate against backend
    const timesheetId = new URLSearchParams(window.location.search).get('timesheetId');
    if (timesheetId) {
      (async () => {
        try {
          setTimesheetLoading(true);
          const base = BASE_URL;
          const res = await fetch(`${base}/api/timesheets/validate-access?timesheetId=${encodeURIComponent(timesheetId)}&accountId=${encodeURIComponent(val)}`, {
            headers: { ...getAuthHeaders() },
          });
          const body = await res.json();
          if (res.ok && body.allowed) {
            localStorage.setItem('accountId', String(val));
            setCurrentUser({ accountId: val });
            setEligibilityMessage('Account allowed — you may proceed to scan.');
            setError("");
          } else {
            setEligibilityMessage("You don't have shifts in this timesheet.");
          }
        } catch (e) {
          console.warn('Error validating account for timesheet', e);
          setEligibilityMessage('Validation failed — try again.');
        } finally {
          setTimesheetLoading(false);
        }
      })();
      return;
    }

    if (allowedAccountIds.size > 0) {
      if (allowedAccountIds.has(val)) {
        localStorage.setItem('accountId', String(val));
        setCurrentUser({ accountId: val });
        setEligibilityMessage('Account allowed — you may proceed to scan.');
        setError("");
      } else {
        setEligibilityMessage("You don't have shifts in this timesheet.");
      }
    } else {
      // No restrictive timesheet context — allow but warn
      localStorage.setItem('accountId', String(val));
      setCurrentUser({ accountId: val });
      setEligibilityMessage('No timesheet context available — proceed with caution.');
      setError("");
    }
  };

  const handleCancel = () => {
    // Stop camera before navigating
    stopCamera();
    // Small delay to ensure camera stops before navigation
    setTimeout(() => {
      navigate(`/checkin${window.location.search}`);
    }, 200);
  };

  useEffect(() => {
    setConfirmAvatarFailed(false);
  }, [showConfirmation, identifiedUser?.accountId, identifiedUser?.profileImageUrl]);

  // Stop camera when confirmation dialog is shown - camera should only be active during scanning
  useEffect(() => {
    if (showConfirmation) {
      setFrozenPreview(null);
      // Stop camera when confirmation dialog is shown
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => {
          track.stop();
          track.enabled = false;
        });
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }
  }, [showConfirmation]);

  if (locationBlockedMessage) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center p-3 overflow-hidden">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
          <h1 className="text-xl font-bold text-center mb-4">Wrong Location</h1>
          <p className="text-center text-gray-700 mb-6">{locationBlockedMessage}</p>
          <button
            onClick={handleCancel}
            className="w-full bg-[#DC1F00] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#b91900] transition-colors"
          >
            TRY AGAIN
          </button>
        </div>
      </div>
    );
  }

  // Show confirmation dialog if face is identified (QR mode or non-QR mode)
  if (showConfirmation && identifiedUser && (qrData || !qrData)) {
    const confirmationAvatarSrc = staffAvatarDisplaySrc(
      identifiedUser.accountId,
      identifiedUser.profileImageUrl
    );
    const showConfirmationAvatarImg =
      Boolean(confirmationAvatarSrc) && !confirmAvatarFailed;

    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center p-3 overflow-hidden">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
          <h1 className="text-xl font-bold text-center mb-4">Confirm Your Identity</h1>

          {/* Profile Picture */}
          <div className="flex justify-center mb-4">
            <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden border-4 border-blue-300">
              {showConfirmationAvatarImg ? (
                <img
                  src={confirmationAvatarSrc}
                  alt="Profile"
                  className="w-full h-full object-cover"
                  onError={() => setConfirmAvatarFailed(true)}
                />
              ) : (
                <span className="text-2xl font-semibold text-gray-500 select-none">
                  {staffInitials(identifiedUser.firstName, identifiedUser.lastName)}
                </span>
              )}
            </div>
          </div>

          {/* User Info */}
          <div className="text-center mb-6">
            <h2 className="text-lg font-semibold text-gray-800">
              {identifiedUser.firstName} {identifiedUser.lastName}
            </h2>
            <p className="text-sm text-gray-600">
              Match confidence: {identifiedUser.similarity.toFixed(1)}%
            </p>
          </div>

          <p className="text-center text-gray-700 mb-6">Is this you?</p>

          {/* Buttons */}
          <div className="space-y-3">
            <button
              onClick={() => handleConfirmIdentity(true)}
              className="w-full bg-[#DC1F00] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#b91900] transition-colors"
            >
              YES, IT'S ME
            </button>

            <button
              onClick={() => handleConfirmIdentity(false)}
              className="w-full bg-gray-300 text-gray-700 py-3 px-4 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
            >
              NO, IT'S NOT ME
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      <div className="flex-1 flex flex-col min-h-0 w-full max-w-[600px] mx-auto px-4 pt-4 pb-4">
        <h1 className="text-center text-2xl font-normal text-gray-700 mb-4 shrink-0">
          {(() => {
            const urlParams = new URLSearchParams(window.location.search);
            return urlParams.has("qrToken")
              ? "QR Check-In - Scan your face"
              : "Scan your face";
          })()}
        </h1>

        {qrData?.clientDetails && (
          <div className="bg-blue-50 p-3 rounded-lg mb-4 text-sm shrink-0">
            <div className="font-semibold text-blue-800">
              {qrData.clientDetails.clientName}
            </div>
            {qrData.clientDetails.clientAddress && (
              <div className="text-blue-600">{qrData.clientDetails.clientAddress}</div>
            )}
            {qrData.shiftDetails && (
              <div className="text-blue-600 mt-1">
                {formatAppDateTime(qrData.shiftDetails.startDate)} -{" "}
                {formatAppDateTime(qrData.shiftDetails.endDate)}
              </div>
            )}
          </div>
        )}

        {/* Full-width camera feed — entire frame is used for face scan (legacy parity) */}
        <div className="relative flex-1 min-h-0 max-h-[55vh] w-full flex flex-col">
          <div className="flex-1 min-h-0 w-full flex items-center justify-center">
            {frozenPreview ? (
              <img
                src={frozenPreview}
                alt="Captured for verification"
                className="w-full max-h-full object-contain"
              />
            ) : (
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full max-h-full min-h-0 object-contain"
              />
            )}
          </div>
          <canvas ref={canvasRef} className="hidden" />

          {/* Face alignment guide — UI only; full camera frame is still captured for scan */}
          {!error && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-48 h-56 sm:w-56 sm:h-64 border-4 border-white rounded-full opacity-50" />
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80 px-6">
              <p className="text-white text-center text-sm">{error}</p>
            </div>
          )}

          {isVerifying && frozenPreview && !error && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 pointer-events-none">
              <span className="px-4 py-2 rounded-full bg-black/80 text-white text-sm font-medium">
                Processing photo…
              </span>
            </div>
          )}

          {!error && (
            <div className="absolute bottom-2 left-0 right-0 flex justify-center pointer-events-none">
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  frozenPreview
                    ? "bg-blue-600 text-white"
                    : isCameraReady
                      ? "bg-green-600 text-white"
                      : "bg-black/70 text-white"
                }`}
              >
                {frozenPreview
                  ? isVerifying
                    ? "Processing photo"
                    : "Photo captured"
                  : isCameraReady
                    ? "Camera ready"
                    : cameraStatus}
              </span>
            </div>
          )}
        </div>

        <div className="shrink-0 mt-2 mb-8">
          <p className="text-center text-sm text-gray-600 mb-3 leading-relaxed">
            Ensure your face is within the photo area and click verify.
            <br />
            All face scans are logged and reviewed for quality purposes.
          </p>

          <div className="space-y-2">
            <button
              onClick={handleVerify}
              disabled={
                !!error ||
                isVerifying ||
                (!frozenPreview && !isCameraReady) ||
                (() => {
                  const urlParams = new URLSearchParams(window.location.search);
                  const hasQrToken = urlParams.has("qrToken");
                  return hasQrToken && !qrData;
                })()
              }
              className="w-full bg-[#DC1F00] text-white py-3 px-4 rounded-lg font-semibold text-base hover:bg-[#b91900] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isVerifying
                ? qrData
                  ? "IDENTIFYING PHOTO..."
                  : "VERIFYING PHOTO..."
                : qrData
                  ? "IDENTIFY FACE"
                  : "VERIFY"}
            </button>

            <button
              onClick={handleCancel}
              className="w-full bg-gray-300 text-gray-700 py-3 px-4 rounded-lg font-semibold text-base hover:bg-gray-400 transition-colors"
            >
              CANCEL
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
